import React, { useState } from "react";
import { Link, NavLink, useLocation } from "react-router-dom";
import {
  BarChart3,
  CreditCard,
  Home,
  LogOut,
  User,
  Menu as MenuIcon,
  HelpCircle,
} from "lucide-react";
import { Menu, Button } from "@mantine/core";
import AnimatedOutlet from "../../../components/AnimatedOutlet";
import { imgNbaLogo } from "../../../assets";

export default function Layout() {
  const location = useLocation();
  const [menuOpened, setMenuOpened] = useState(false);

  const isActive = (path: string) => {
    if (path === "/d") {
      return location.pathname === "/d";
    }
    return location.pathname.startsWith(path);
  };

  const menuItems = [
    { icon: Home, label: "Home", path: "/d" },
    { icon: CreditCard, label: "Transactions", path: "/d/transactions" },
    { icon: BarChart3, label: "Deals", path: "/d/deals" },
    { icon: User, label: "Profile", path: "/d/settings" },
  ];

  return (
    <div className="dashboard">
      <aside
        className="dashboard__sidebar shadow-[var(--card-shadow)]"
        aria-label="Dashboard navigation"
      >
        <div className="dashboard__brand">
          <img className="dashboard__logo" src={imgNbaLogo} alt="NBA logo" />
          <div className="dashboard__brand-text">
            <div className="dashboard__brand-name">
              NIGERIAN BAR ASSOCIATION
            </div>
            <div className="dashboard__brand-sub">REMUNERATION PORTAL</div>
          </div>
        </div>

        {/* Desktop Navigation */}
        <nav className="dashboard__nav dashboard__nav--desktop">
          <NavLink
            to="/d"
            end
            className={({ isActive }) =>
              isActive
                ? "dashboard__nav-link dashboard__nav-link--active"
                : "dashboard__nav-link"
            }
          >
            <Home size={20} aria-hidden="true" />
            <span>Home</span>
          </NavLink>

          <NavLink
            to="/d/transactions"
            className={({ isActive }) =>
              isActive
                ? "dashboard__nav-link dashboard__nav-link--active"
                : "dashboard__nav-link"
            }
          >
            <CreditCard size={20} aria-hidden="true" />
            <span>Transactions</span>
          </NavLink>

          <NavLink
            to="/d/deals"
            className={({ isActive }) =>
              isActive
                ? "dashboard__nav-link dashboard__nav-link--active"
                : "dashboard__nav-link"
            }
          >
            <BarChart3 size={20} aria-hidden="true" />
            <span>Deals</span>
          </NavLink>

          <NavLink
            to="/d/settings"
            className={({ isActive }) =>
              isActive
                ? "dashboard__nav-link dashboard__nav-link--active"
                : "dashboard__nav-link"
            }
          >
            <User size={20} aria-hidden="true" />
            <span>Profile</span>
          </NavLink>

          <NavLink
            to="/d/support"
            className={({ isActive }) =>
              isActive
                ? "dashboard__nav-link dashboard__nav-link--active"
                : "dashboard__nav-link"
            }
          >
            <HelpCircle size={20} aria-hidden="true" />
            <span>Support</span>
          </NavLink>

          <Link
            className="dashboard__nav-link transition-transform duration-200 hover:-translate-y-0.5"
            to="/login"
          >
            <LogOut size={20} aria-hidden="true" />
            <span>Logout</span>
          </Link>
        </nav>

        {/* Mobile Navigation Menu */}
        <div className="dashboard__nav--mobile">
          <Menu
            shadow="md"
            width={200}
            position="bottom-start"
            opened={menuOpened}
            onChange={setMenuOpened}
          >
            <Menu.Target>
              <Button
                variant="filled"
                color="var(--nba-green)"
                leftSection={<MenuIcon size={18} />}
                className="dashboard__mobile-menu-button"
                styles={{
                  root: {
                    width: "100%",
                    backgroundColor: "rgba(31, 211, 108, 0.2)",
                    color: "#fff",
                    border: "1px solid rgba(31, 211, 108, 0.3)",
                    "&:hover": {
                      backgroundColor: "rgba(31, 211, 108, 0.3)",
                    },
                  },
                }}
              >
                Menu
              </Button>
            </Menu.Target>

            <Menu.Dropdown>
              {menuItems.map((item) => {
                const Icon = item.icon;
                const active = isActive(item.path);
                return (
                  <Menu.Item
                    key={item.path}
                    component={Link}
                    to={item.path}
                    leftSection={<Icon size={18} />}
                    onClick={() => setMenuOpened(false)}
                    style={{
                      backgroundColor: active
                        ? "rgba(31, 211, 108, 0.2)"
                        : "transparent",
                      fontWeight: active ? 600 : 400,
                    }}
                  >
                    {item.label}
                  </Menu.Item>
                );
              })}
              <Menu.Divider />
              <Menu.Item
                component={Link}
                to="/login"
                leftSection={<LogOut size={18} />}
                onClick={() => setMenuOpened(false)}
                color="red"
              >
                Logout
              </Menu.Item>
            </Menu.Dropdown>
          </Menu>
        </div>
      </aside>

      <main className="dashboard__main">
        <AnimatedOutlet />
      </main>
    </div>
  );
}
